import React, { useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Avatar from '@material-ui/core/Avatar'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Hidden from '@material-ui/core/Hidden'
import { makeStyles } from '@material-ui/core/styles'
import { signOutUser } from '../../store/actions/user'
import { hasActiveAccess, isInTrial, getDaysRemaining } from '../../utils/membershipUtils'
import { shouldShowUpgradeSurfaces } from '../../utils/onboardingUtils'
import StoryworldMainNav from './StoryworldMainNav'

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    backgroundColor: '#faf7fc',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    boxShadow: 'none',
  },
  toolbar: {
    minHeight: 64,
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      minHeight: 56,
    },
  },
  logo: {
    height: 48,
    width: 'auto',
    cursor: 'pointer',
    display: 'block',
  },
  grow: {
    flexGrow: 1,
    minWidth: theme.spacing(1),
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
}))

function userInitials(user) {
  const n = (user && (user.fullName || user.username || '')).trim()
  if (!n) return '?'
  const parts = n.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return n.slice(0, 2).toUpperCase()
}

export default function AuthenticatedAppBar() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state.user)
  const [anchorEl, setAnchorEl] = useState(null)

  const closeMenu = () => setAnchorEl(null)

  const onLogout = () => {
    closeMenu()
    dispatch(signOutUser()).then(() => history.push('/login'))
  }

  if (!user) return null

  const showUpgradeChip =
    !hasActiveAccess(user) && shouldShowUpgradeSurfaces()

  return (
    <AppBar className={classes.appBar} color="default" position="sticky">
      <Toolbar className={classes.toolbar} disableGutters style={{ paddingLeft: 8, paddingRight: 8 }}>
        <Button component={Link} to="/tonight" style={{ minWidth: 0, padding: 4 }}>
          <img
            className={classes.logo}
            src="/media/logo.png"
            alt="WeeStoryWorld"
          />
        </Button>

        <div className={classes.grow} />

        <Hidden smDown>
          <StoryworldMainNav />
        </Hidden>

        <Hidden mdUp>
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '10px 8px max(10px, env(safe-area-inset-bottom))', background: '#fffcf7', borderTop: '1px solid #e6deed', zIndex: 1200 }}>
            <StoryworldMainNav />
          </div>
        </Hidden>

        <div className={classes.grow} />

        <div className={classes.right}>
          {hasActiveAccess(user) ? (
            user.membership ? (
              <Chip size="small" label="Premium" style={{ backgroundColor: '#c6b3dd', color: '#1a1a1a' }} />
            ) : isInTrial(user) ? (
              <Chip
                size="small"
                label={`Trial · ${getDaysRemaining(user)}d`}
                style={{ backgroundColor: '#fff3e0', color: '#e65100' }}
              />
            ) : null
          ) : showUpgradeChip ? (
            <Chip
              size="small"
              label="Unlock Premium"
              component={Link}
              to="/membership"
              clickable
              style={{ borderColor: '#c6b3dd', color: '#1a1a1a' }}
              variant="outlined"
            />
          ) : null}

          <IconButton
            aria-label="Account menu"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            style={{ padding: 2 }}
          >
            <Avatar
              src={user.imageUrl || user.image || undefined}
              style={{ width: 40, height: 40, fontSize: 14, backgroundColor: '#c6b3dd', color: '#1a1a1a' }}
            >
              {!user.imageUrl && !user.image ? userInitials(user) : null}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            getContentAnchorEl={null}
          >
            <MenuItem component={Link} to="/profile" onClick={closeMenu}>
              My Profile
            </MenuItem>
            <MenuItem component={Link} to="/settings" onClick={closeMenu}>
              Settings
            </MenuItem>
            <MenuItem component={Link} to="/membership" onClick={closeMenu}>
              Membership
            </MenuItem>
            <MenuItem component={Link} to="/story/create?mode=simple" onClick={closeMenu}>
              Create story
            </MenuItem>
            <Divider />
            <MenuItem onClick={onLogout}>Log Out</MenuItem>
          </Menu>
        </div>
      </Toolbar>
    </AppBar>
  )
}
